import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  DestinationAttribute,
  MappingRule,
  Operand,
  OperatorGroups,
} from '@em/shared/mapping-engine/domain-models';
import {expandCollapseAnimation} from '@em/shared/ui';
import {combineLatest, Observable, of} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MappingEngineFormBuilderService} from '../../services/mapping-engine-form-builder/mapping-engine-form-builder.service';

@Component({
  selector: 'em-mapping-rule-list',
  templateUrl: './mapping-rule-list.component.html',
  styleUrls: ['./mapping-rule-list.component.scss'],
  animations: [expandCollapseAnimation],
  standalone: false,
})
export class EmMappingRuleListComponent implements OnInit {
  private readonly _fb = inject(MappingEngineFormBuilderService);
  private readonly _destroyRef = inject(DestroyRef);

  @Input() arrayIndex: number | undefined;
  @Input() destinationAttributes: DestinationAttribute[] | null | undefined;
  @Input() sourceAttributes: string[] | null | undefined;
  @Input() customSourceAttributes: string[] | null | undefined;
  @Input() operatorGroups: OperatorGroups | undefined;
  @Input() forceCustomWithoutFlag = false;
  @Input() showAdvancedSettings = true;
  @Input() allowChangeDestination = true;

  toggleAdvancedSettings = false;
  mappingRulesFormGroup: UntypedFormGroup | null | undefined;
  isRequiredAttribute$: Observable<boolean> | undefined;
  showFixedDestination$: Observable<boolean> | undefined;
  availableAttributes$ = this._fb.getAllAddedAttributes().pipe(
    map((allAdded) => {
      const notAddedAttributes: string[] =
        this.destinationAttributes
          ?.filter((attr) => allAdded.indexOf(attr.attribute) === -1)
          .map((attr) => attr.attribute) || [];

      const unsortedList = this.attributeControl?.value
        ? [...notAddedAttributes, this.attributeControl?.value]
        : notAddedAttributes;

      return unsortedList.sort();
    }),
  );

  //#region Properties
  get attributeControl(): UntypedFormControl {
    return this.mappingRulesFormGroup?.get('attribute') as UntypedFormControl;
  }
  get attributeControlValue(): Operand | null {
    return this.mappingRulesFormGroup?.get('attribute')?.value as Operand;
  }
  get mappingRulesFormArray(): UntypedFormArray {
    return this.mappingRulesFormGroup?.get('mappingRules') as UntypedFormArray;
  }
  get stripHtmlControl(): UntypedFormControl {
    return this.mappingRulesFormGroup?.get('stripHTML') as UntypedFormControl;
  }
  get customControl(): UntypedFormControl {
    return this.mappingRulesFormGroup?.get('custom') as UntypedFormControl;
  }
  get showCustomInput(): boolean {
    return this.forceCustomWithoutFlag || this.customControl.value;
  }

  ngOnInit(): void {
    this.mappingRulesFormGroup = this._fb.getMappingRuleList(
      this.arrayIndex || 0,
    );

    this.mappingRulesFormArray.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newRules: MappingRule[]) => {
        if (
          newRules.length === 1 &&
          newRules[0].conditionalStatementList?.conditionalStatements?.length
        ) {
          this.addElseRule();
        }

        if (
          newRules.length > 1 &&
          !newRules[0].conditionalStatementList?.conditionalStatements?.length
        ) {
          this.reamoveAllExpceptFirst();
        }
      });

    this.isRequiredAttribute$ = this.attributeControl.valueChanges.pipe(
      startWith(this.attributeControl.value),
      map(
        (attribute) =>
          !this.customControl.value &&
          !!this.destinationAttributes?.find(
            (attr) => attr.attribute === attribute,
          )?.required,
      ),
    );

    this.showFixedDestination$ = combineLatest([
      this.isRequiredAttribute$,
      of(this.allowChangeDestination),
    ]).pipe(map(([required, allowChange]) => required || !allowChange));
  }

  addNewRule() {
    this._fb.addMappingRule(
      {
        mappingType: 'attribute',
        conditionalStatementList: {
          conditionalStatements: [{conditions: [{}], logicalOperator: null}],
          logicalOperator: null,
        },
      } as MappingRule,
      this.mappingRulesFormArray,
      this.mappingRulesFormArray.length - 1,
    );
  }

  addElseRule() {
    this._fb.addMappingRule(
      {
        mappingType: 'leave_empty',
      } as MappingRule,
      this.mappingRulesFormArray,
    );
  }

  removeRule(index: number) {
    this.mappingRulesFormArray.removeAt(index);
  }

  reamoveAllExpceptFirst() {
    this._fb.reamoveAllExpceptFirst(this.mappingRulesFormArray);
  }

  convertToNonCustom() {
    this.customControl.setValue(false);
    this.customControl.updateValueAndValidity();
  }
}
